import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { AppState, clearStore } from '../../../app/app.reducer';
import { AuthenticationAdapter } from '../../adapters/authentication.adapter';
import { resetTosSkip } from '../config/config.actions';
import {
  loginFailure,
  loginSuccess,
  loginWithEmail,
  loginWithGoogle,
  logout,
  logoutFailure,
  logoutSuccess,
  refreshToken,
  refreshTokenFailure,
  refreshTokenSuccess,
} from './auth.actions';

const controller = new AbortController();

@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly authAdapter = inject(AuthenticationAdapter);
  private readonly router = inject(Router);
  private readonly cookieService = inject(CookieService);
  private readonly store = inject(Store<AppState>);
  
  // Listen for the logout event on other tabs
  private storageEventListener(event: StorageEvent) {
    if (event.storageArea == localStorage) {
      if (event?.key && event.key == 'logout-event') {
        this.store.dispatch(logout());
      }
    }
  }

  loginWithEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginWithEmail),
      switchMap((props) =>
        this.authAdapter
          .login({
            email: props.email,
            password: props.password,
            tfa: props.tfa,
          })
          .pipe(
            map((response) =>
              loginSuccess({ token: response.jwt, user: response.user })
            ),
            catchError((error) => {
              return of(loginFailure({ error }));
            })
          )
      )
    )
  );

  loginWithGoogle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginWithGoogle),
      switchMap((props) =>
        this.authAdapter.signInWithGoogle(props.token).pipe(
          map((response) =>
            loginSuccess({ token: response.jwt, user: response.user })
          ),
          catchError((error) => {
            return of(loginFailure({ error }));
          })
        )
      )
    )
  );

  loginFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginFailure),
        tap(() => {
          // Todo: Add proper error handling. Maybe with a toasty?
          //alert(action.error.message);
        })
      ),
    { dispatch: false }
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginSuccess),
        tap(() => {
          // logout other tabs on login
          window.localStorage.setItem('logout-event', uuidv4());
          // delay adding the event listener to prevent being logged out immediately when other tabs are opened
          setTimeout(() => {
            window.addEventListener(
              'storage',
              this.storageEventListener.bind(this),
              { signal: controller.signal }
            );
          }, 5000);
          this.router.navigate(['/dashboard']);
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      switchMap(() =>
        this.authAdapter.logout().pipe(
          map(() => {
            // logout other tabs on logout and remove event listener to prevent duplicates
            controller.abort();
            window.localStorage.setItem('logout-event', uuidv4());
            return logoutSuccess();
          }),
          catchError((error) => {
            return of(logoutFailure({ error }));
          })
        )
      )
    )
  );

  logoutFailure$ = createEffect(
    () => this.actions$.pipe(ofType(logoutFailure)),
    { dispatch: false }
  );

  logoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logoutSuccess),
        tap(() => {
          this.cookieService.deleteAll('/');
          this.store.dispatch(resetTosSkip());
          this.store.dispatch(clearStore());
          // Preserve all current URL parameters when redirecting to login
          const currentParams = new URLSearchParams(window.location.search);
          const loginUrl = new URL('/auth/login', window.location.origin);
          currentParams.forEach((value, key) => {
            loginUrl.searchParams.set(key, value);
          });
          // Force a complete page redirect to the login page
          window.location.replace(loginUrl.toString());
        })
      ),
    { dispatch: false }
  );

  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(refreshToken),
      exhaustMap((payload) =>
        this.authAdapter.refreshToken(payload.polling).pipe(
          map((response) => refreshTokenSuccess({ token: response.jwt })),
          catchError((error) => {
            return of(refreshTokenFailure({ error }));
          })
        )
      )
    )
  );

  refreshTokenFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(refreshTokenFailure),
        tap((payload) => {
          const error = payload.error;
          if (error instanceof HttpErrorResponse) {
            if (error.status.toString().startsWith('4')) {
              // Set URL param to indicate session timeout before dispatching logout action
              const url = new URL(window.location.href);
              //url.searchParams.set('session-timeout', 'true');
              window.history.replaceState({}, '', url.toString());
              this.store.dispatch(logout());
            }
          }
        })
      ),
    { dispatch: false }
  );
}
